import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowDown,
  ArrowLeft,
  AtSign,
  Info,
  Lock,
  Paperclip,
  Search,
  SquareKanban,
  UserX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLocale } from "@/hooks/useLocale";
import { useOpenChatDirectMutation } from "@/services/(my-space)/api.chat";
import { useImagePreview } from "@/context/ImagePreview";
import { useUrlParam } from "@/hooks/useTableParams";
import { openChatWindow } from "@/components/layout/chat-windows/chat-windows-store";
import { chatHub } from "@/services/websocket/chat-hub";
import { getErrorMessage } from "@/utils/error.utils";
import { cn } from "@/utils/cn";
import type { IChatLocalMessage, IChatMessage, IChatUserSummary } from "@/types/(my-space)/chat.interface";
import { useChatRoom } from "../_hooks/useChatRoom";
import { useScheduledMessages } from "../_hooks/useScheduledMessages";
import { useMentionQueue } from "../_hooks/useMentionQueue";
import { messageKey, useFreshMessages } from "../_hooks/useFreshMessages";
import { usePresenceVersion, useWatchPresence } from "../_hooks/usePresence";
import {
  conversationTitle,
  dayLabel,
  memberCountLabel,
  peerAvailabilityBanner,
  presenceLabel,
} from "../_lib/chat-labels";
import { messageReceipts } from "../_lib/chat-utils";
import { tickStateIn } from "../_lib/ticks";
import { playedByAllIn } from "../_lib/voice-played";
import { pinnedBarItems } from "../_lib/pinned";
import { PinnedBar } from "./PinnedBar";
import { ForwardDialog } from "./ForwardDialog";
import { ProfilePopup, type ProfileSubject } from "./ProfilePopup";
import { personProfile } from "../_lib/profile-subject";
import { chatWallpaperSpec } from "../_lib/chat-wallpapers";
import { ChatAvatar } from "./ChatAvatar";
import { MessageBubble } from "./MessageBubble";
import { Composer, type ComposerHandle } from "./Composer";
import { ChatInfoDrawer } from "./ChatInfoDrawer";
import { CreateTicketDialog } from "./CreateTicketDialog";
import { MessageInfoDialog } from "./MessageInfoDialog";
import { ScheduledBar, ScheduledMessagesDrawer } from "./ScheduledMessages";
import { MessageSearchBar, MessageSearchResults } from "./MessageSearch";
import { channelOf, channelReadOnly, channelSubtitle, isOfficialChannel } from "../_lib/channel-notices";
import { VerifiedBadge } from "./VerifiedBadge";

/** Presence subscriptions are capped per request; a huge group watches its first 200. */
const PRESENCE_WATCH_CAP = 200;
const NEAR_BOTTOM_PX = 160;
/** How long a jumped-to message stays tinted (matches `animate-message-flash`). */
const FLASH_MS = 1500;

type ScrollIntent = { type: "bottom" } | { type: "message"; id: number; smooth: boolean };

interface ChatRoomProps {
  conversationId: number;
  onBack: () => void;
  /**
   * "window": inside a floating chat window — no back button, Chat info kept
   * in the window rather than the page's URL, and [headerActions] (the
   * window's own buttons) at the end of the header.
   */
  variant?: "page" | "window";
  headerActions?: ReactNode;
}

/** Consecutive messages of one calendar day (the list runs oldest → newest). */
const messagesByDay = <T extends { createdAt: string }>(items: T[]) => {
  const days: { key: string; first: number; items: T[] }[] = [];
  items.forEach((m, i) => {
    const key = new Date(m.createdAt).toDateString();
    const last = days[days.length - 1];
    if (last?.key === key) last.items.push(m);
    else days.push({ key, first: i, items: [m] });
  });
  return days;
};

export const ChatRoom = ({ conversationId, onBack, variant = "page", headerActions }: ChatRoomProps) => {
  const inWindow = variant === "window";
  const { t } = useTranslation("chat");
  const { formatDate, formatNumber } = useLocale();
  const navigate = useNavigate();
  const room = useChatRoom(conversationId);
  const { detail, messages, myUserId } = room;
  const fresh = useFreshMessages(
    conversationId,
    messages,
    room.isLoading,
    room.windowVersion,
  );
  const scheduled = useScheduledMessages(conversationId);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const closeScheduled = useCallback(() => setScheduledOpen(false), []);
  const composerRef = useRef<ComposerHandle>(null);
  // In the URL so a reload, or the profile popup's Info, opens it. A window
  // keeps its own: the page behind it has nothing to do with this chat.
  const [infoParam, setInfoParam] = useUrlParam("info", "");
  const [windowInfo, setWindowInfo] = useState(false);
  const infoOpen = inWindow ? windowInfo : infoParam === "1";
  const setInfoOpen = (open: boolean) =>
    inWindow ? setWindowInfo(open) : setInfoParam(open ? "1" : "");
  const [toDelete, setToDelete] = useState<IChatMessage | null>(null);
  const [infoFor, setInfoFor] = useState<IChatMessage | null>(null);
  const [forwarding, setForwarding] = useState<IChatMessage | null>(null);
  /** Messages picked for a project ticket; null: not picking. */
  const [ticketPick, setTicketPick] = useState<Set<number> | null>(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const startTicket = useCallback((m: IChatMessage) => setTicketPick(new Set([m.id])), []);
  const toggleTicketPick = useCallback(
    (m: IChatMessage) =>
      setTicketPick((s) => {
        if (!s) return s;
        const next = new Set(s);
        if (next.has(m.id)) next.delete(m.id);
        else next.add(m.id);
        return next;
      }),
    [],
  );
  const endTicket = useCallback(() => {
    setTicketOpen(false);
    setTicketPick(null);
  }, []);
  // Another chat: nothing stays picked.
  useEffect(() => endTicket(), [conversationId, endTicket]);
  /** The profile popup: a sender from a bubble, or this chat (header picture). */
  const [profile, setProfile] = useState<{ subject: ProfileSubject; isChat: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);
  // In-conversation search. Local, not in the URL: the list's params ride
  // along when switching rooms, and a query belongs to this room only.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [openDirect] = useOpenChatDirectMutation();
  const openPreview = useImagePreview();
  const openImages = useCallback(
    (urls: string[], index: number) => openPreview(urls, undefined, index),
    [openPreview],
  );

  // ── Presence ────────────────────────────────────────────────────────────
  const watched = useMemo(() => {
    // A self chat's peer is me: nobody to watch.
    if (!detail || detail.isSelf) return [];
    if (detail.type === "direct") return detail.peer ? [detail.peer.id] : [];
    return detail.members
      .map((m) => m.id)
      .filter((id) => id !== myUserId)
      .slice(0, PRESENCE_WATCH_CAP);
  }, [detail, myUserId]);
  useWatchPresence(watched);
  usePresenceVersion();

  // The background stays put while messages scroll over it.
  const wallpaper = chatWallpaperSpec(detail?.effectiveWallpaper);

  const headerStatus = (() => {
    if (!detail) return null;
    if (detail.isSelf) return t("labels.self_subtitle");
    if (detail.type === "channel") return channelSubtitle(t, channelOf(detail));
    if (room.typingLabel) return room.typingLabel;
    if (detail.type === "direct") {
      if (detail.peer && !detail.peer.isActive) return t("labels.account_inactive");
      return presenceLabel(t, formatDate, chatHub.getPresence(detail.peer?.id));
    }
    const members = memberCountLabel(t, detail);
    const online = watched.filter((id) => chatHub.getPresence(id)?.online).length;
    return online ? t("status.group_online", { members, online }) : members;
  })();

  // ── Scrolling ───────────────────────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);
  const prevHeightRef = useRef<number | null>(null);
  const lastMessageKey = messages.length ? `${messages[messages.length - 1].id}` : "";
  const lastIsMine = messages.length ? messages[messages.length - 1].sender?.id === myUserId : false;

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  // First page: start at the newest message.
  useLayoutEffect(() => {
    if (!room.isLoading) scrollToBottom();
  }, [room.isLoading, conversationId, scrollToBottom]);

  // New message at the bottom: follow it if we were there, or it's ours.
  // Not after a jump or a newer page: that's a load, positioned on purpose.
  const followedVersionRef = useRef(room.windowVersion);
  useLayoutEffect(() => {
    if (followedVersionRef.current !== room.windowVersion) {
      followedVersionRef.current = room.windowVersion;
      return;
    }
    if (prevHeightRef.current != null) return;
    if (atBottomRef.current || lastIsMine) scrollToBottom();
  }, [lastMessageKey, lastIsMine, room.windowVersion, scrollToBottom]);

  // Where to scroll once the window holds what was asked for (a jump's
  // target, or the end after re-attaching).
  const scrollIntentRef = useRef<ScrollIntent | null>(null);
  const [scrollRequest, setScrollRequest] = useState(0);
  const [flash, setFlash] = useState<{ id: number; nonce: number } | null>(null);
  useLayoutEffect(() => {
    const intent = scrollIntentRef.current;
    const el = scrollRef.current;
    if (!intent || !el || room.isLoading) return;
    if (intent.type === "bottom") {
      scrollIntentRef.current = null;
      scrollToBottom();
      return;
    }
    const node = el.querySelector<HTMLElement>(`[data-message-id="${intent.id}"]`);
    if (!node) return; // not rendered yet — the next render tries again
    scrollIntentRef.current = null;
    node.scrollIntoView({ block: "center", behavior: intent.smooth ? "smooth" : "auto" });
    setFlash({ id: intent.id, nonce: Date.now() });
  }, [messages, room.isLoading, scrollRequest, scrollToBottom]);

  useEffect(() => {
    if (!flash) return;
    const timer = setTimeout(() => setFlash(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flash]);

  // Read through a ref so showMessage (a MessageBubble prop) stays stable.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const { jumpTo, jumpToLatest } = room;
  /** Shows message `id`: scrolls to it, loading the part of the chat around it if needed. */
  const showMessage = useCallback(
    async (id: number) => {
      const loaded = messagesRef.current.some((m) => m.id === id);
      // An older page still in flight belongs to the window being replaced.
      if (!loaded) prevHeightRef.current = null;
      const ok = await jumpTo(id);
      if (!ok) return;
      scrollIntentRef.current = { type: "message", id, smooth: loaded };
      setScrollRequest((n) => n + 1);
    },
    [jumpTo],
  );

  // Opened from a search result (?message=<id>): once the chat has loaded,
  // show that message, then drop the param so a reload doesn't jump again.
  const [jumpParam, setJumpParam] = useUrlParam("message", "");
  useEffect(() => {
    const id = Number(jumpParam);
    if (inWindow || !id || room.isLoading || !detail) return;
    setJumpParam("");
    void showMessage(id);
  }, [inWindow, jumpParam, room.isLoading, detail, showMessage, setJumpParam]);

  const goToLatest = useCallback(async () => {
    if (!room.isDetached) {
      scrollToBottom(true);
      return;
    }
    prevHeightRef.current = null;
    if (await jumpToLatest()) {
      scrollIntentRef.current = { type: "bottom" };
      setScrollRequest((n) => n + 1);
    }
  }, [jumpToLatest, room.isDetached, scrollToBottom]);

  // ── Jump to my mentions ─────────────────────────────────────────────────
  const { isDetached } = room;
  const mentionQueue = useMentionQueue({
    conversationId,
    myUserId,
    detail,
    detailReady: room.detailReady,
    isFollowingLive: () =>
      !isDetached && atBottomRef.current && document.visibilityState === "visible",
    scrollRef,
    messages,
    observe: !room.isLoading && !searchOpen,
  });
  const { takeOldest } = mentionQueue;
  const goToMention = useCallback(() => {
    const id = takeOldest();
    if (id != null) void showMessage(id);
  }, [showMessage, takeOldest]);
  const mentionLabel =
    mentionQueue.count > 1
      ? t("actions.jump_to_mention_count", { count: mentionQueue.count })
      : t("actions.jump_to_mention");

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery("");
    requestAnimationFrame(() => searchButtonRef.current?.focus());
  }, []);

  const openResult = useCallback(
    (m: IChatMessage) => {
      // The query stays: reopening search shows the same results.
      setSearchOpen(false);
      void showMessage(m.id);
    },
    [showMessage],
  );

  // Older page prepended: keep the viewport on the same message.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && prevHeightRef.current != null && !room.isLoadingOlder) {
      el.scrollTop += el.scrollHeight - prevHeightRef.current;
      prevHeightRef.current = null;
    }
  }, [messages, room.isLoadingOlder]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    atBottomRef.current = bottom;
    setAtBottom(bottom);
    if (room.isJumping) return;
    if (el.scrollTop < 200 && room.hasMore && !room.isLoadingOlder) {
      prevHeightRef.current = el.scrollHeight;
      void room.loadOlder();
    }
    // A detached window grows newer as you reach its end, until it re-attaches.
    if (bottom && room.isDetached && !room.isLoadingNewer) void room.loadNewer();
  };

  // ── Read receipts ───────────────────────────────────────────────────────
  // A self chat has none: a single "sent" tick and no Message info.
  const receiptsFor = useCallback(
    (m: IChatMessage) => messageReceipts(detail, m, myUserId),
    [detail, myUserId],
  );

  // Own messages' ticks: sent / delivered to everyone / read by everyone.
  const tickFor = useCallback(
    (m: IChatLocalMessage) => tickStateIn(detail, m, myUserId),
    [detail, myUserId],
  );

  // ── Actions ─────────────────────────────────────────────────────────────
  /** Opens our direct chat with [userId] — with its Chat info showing when [withInfo]. */
  const messageUser = useCallback(
    async (userId: number, withInfo = false) => {
      if (detail?.type === "direct" && detail.peer?.id === userId) {
        setInfoOpen(withInfo);
        return;
      }
      try {
        const conversation = await openDirect(userId).unwrap();
        setInfoOpen(false);
        // From a window, their chat opens in a window of its own.
        if (inWindow && !withInfo) openChatWindow(conversation.id);
        else navigate(`/chat/${conversation.id}${withInfo ? "?info=1" : ""}`);
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.open")));
      }
    },
    [detail, inWindow, navigate, openDirect, t],
  );

  const isThisChat = !!profile?.isChat;
  const openSender = useCallback(
    (sender: IChatUserSummary) => setProfile({ subject: personProfile(sender), isChat: false }),
    [],
  );

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(t("actions.copied"));
      } catch {
        // Clipboard blocked — nothing useful to report.
      }
    },
    [t],
  );

  const onReply = useCallback((m: IChatMessage) => composerRef.current?.startReply(m), []);
  const onEdit = useCallback((m: IChatMessage) => composerRef.current?.startEdit(m), []);

  // ── Render ──────────────────────────────────────────────────────────────
  if (room.loadError && !messages.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-muted-foreground">{room.loadError}</p>
        <Button variant="outline" size="sm" onClick={() => void room.reload()}>
          {t("page.try_again")}
        </Button>
      </div>
    );
  }

  const title = detail ? conversationTitle(t, detail) : "";
  const peer = detail?.peer ?? null;
  const isDirect = detail?.type === "direct";
  const isSelf = !!detail?.isSelf;
  // My own work status is no reason to warn me about writing to myself.
  const banner = isDirect && !isSelf && peer?.isActive ? peerAvailabilityBanner(t, peer) : null;
  const canSend = !!detail?.canSend && !(isDirect && peer && !peer.isActive);
  // A broadcast's recipients don't see each other; everyone else sees the members.
  const canSeeVoters = !!detail && (detail.type !== "broadcast" || detail.canSend);
  // Cheap (≤3 pins); a plain call because this sits past the early returns.
  const pins = pinnedBarItems(detail?.pinnedMessages, messages);

  return (
    <div
      className="relative flex h-full min-h-0 flex-col"
      onDragOver={(e) => {
        if (!canSend || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(e) => {
        if (!canSend) return;
        e.preventDefault();
        setDragging(false);
        composerRef.current?.addFiles([...e.dataTransfer.files]);
      }}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center border-b border-border bg-background",
          inWindow ? "gap-2 px-2.5 py-2" : "gap-3 px-3 py-2.5 md:px-5",
        )}
      >
        {searchOpen ? (
          <div className="flex h-10 min-w-0 flex-1 items-center">
            <MessageSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClose={closeSearch}
            />
          </div>
        ) : (
          <>
            {inWindow ? null : (
              <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label={t("a11y.back")} className="lg:hidden">
                <ArrowLeft className="size-4 rtl-flip" />
              </Button>
            )}
            {detail ? (
              <div className="flex min-w-0 flex-1 items-center gap-3">
              {/* The picture opens its popup (like the list); the name opens Chat info. */}
              <button
                type="button"
                // A channel has no profile: its picture opens its settings.
                onClick={() =>
                  detail.type === "channel"
                    ? setInfoOpen(true)
                    : setProfile({ subject: detail, isChat: true })
                }
                aria-label={t("profile.open", { name: title })}
                className="shrink-0 rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
              >
                <ChatAvatar
                  name={title}
                  imageUrl={isDirect ? peer?.profileImage : detail.imageUrl}
                  kind={detail.type}
                  channel={channelOf(detail)}
                  size="md"
                  isOnline={isDirect && !isSelf && !!chatHub.getPresence(peer?.id)?.online}
                />
              </button>
              <button
                type="button"
                onClick={() => setInfoOpen(true)}
                className="flex min-w-0 flex-1 items-center text-start"
                aria-label={t("a11y.conversation_info")}
              >
                <div className="flex min-w-0 flex-col">
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-sm font-semibold text-foreground">{title}</span>
                    {isOfficialChannel(channelOf(detail)) ? <VerifiedBadge /> : null}
                  </span>
                  {headerStatus ? (
                    <span
                      className={cn(
                        "truncate text-xs",
                        room.typingLabel ? "font-medium text-primary" : "text-muted-foreground",
                      )}
                    >
                      {headerStatus}
                    </span>
                  ) : null}
                </div>
              </button>
              </div>
            ) : (
              <div className="flex flex-1 items-center gap-3">
                <Skeleton className="size-10 rounded-full" />
                <Skeleton className="h-4 w-40" />
              </div>
            )}
            {detail ? (
              <Button
                ref={searchButtonRef}
                variant="ghost"
                size="icon-sm"
                onClick={() => setSearchOpen(true)}
                aria-label={t("search.open")}
                title={t("search.open")}
              >
                <Search className="size-4" />
              </Button>
            ) : null}
            {detail ? (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setInfoOpen(true)}
                aria-label={t("a11y.conversation_info")}
                title={t("info.title")}
              >
                <Info className="size-4" />
              </Button>
            ) : null}
            {headerActions}
          </>
        )}
      </div>

      {pins.length && !searchOpen ? (
        <PinnedBar
          pins={pins}
          canUnpin={canSend}
          onShow={showMessage}
          onUnpin={room.togglePin}
          onReorder={room.reorderPins}
        />
      ) : null}

      {/* Messages (and, while searching, the results over them) */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className={cn(
            "relative h-full overflow-y-auto py-3",
            !wallpaper.style && "bg-muted/30",
          )}
          style={wallpaper.style}
          // Covered by the search results: out of the tab order meanwhile.
          inert={searchOpen || undefined}
        >
          {room.isLoading ? (
            <MessagesSkeleton />
          ) : (
            <>
              {room.isLoadingOlder ? (
                <div className="flex justify-center py-2">
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
              ) : null}
              {messages.length === 0 ? (
                <div className="flex h-full items-center justify-center p-6">
                  <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                    {t("labels.new_chat")}
                  </span>
                </div>
              ) : (
                // One block per day, its date pill inside: a sticky pill
                // sticks only within its block, so each day's pill scrolls
                // away with that day instead of piling up at the top.
                messagesByDay(messages).map((day) => (
                  <section key={day.key}>
                    <div className="sticky top-1 z-10 flex justify-center py-2">
                      <span className="rounded-full border border-border bg-background px-3 py-0.5 text-[11px] font-medium text-muted-foreground shadow-xs">
                        {dayLabel(t, formatDate, day.items[0].createdAt)}
                      </span>
                    </div>
                    {day.items.map((m, j) => {
                      const i = day.first + j;
                      const prev = messages[i - 1];
                      const newDay = j === 0;
                      const isMine = m.sender?.id === myUserId;
                      const showSender =
                        !isMine &&
                        !isDirect &&
                        (newDay || !prev || prev.type === "system" || prev.sender?.id !== m.sender?.id);
                      return (
                        <Fragment key={messageKey(m)}>
                          <div
                            data-message-id={m.id > 0 ? m.id : undefined}
                            className={cn(
                              "relative",
                              // A message that just arrived pops in from its bubble's corner.
                              fresh.has(messageKey(m)) && "animate-message-in",
                              isMine
                                ? "origin-bottom-right rtl:origin-bottom-left"
                                : "origin-bottom-left rtl:origin-bottom-right",
                            )}
                          >
                            {flash?.id === m.id ? (
                              // Keyed by the jump, so jumping to it again replays the pulse.
                              <span
                                key={flash.nonce}
                                aria-hidden
                                className="pointer-events-none absolute inset-0 animate-message-flash"
                              />
                            ) : null}
                            <MessageBubble
                              message={m as IChatLocalMessage}
                              isMine={isMine}
                              showSender={showSender}
                              avatarColumn={!isDirect}
                              compact={inWindow}
                              tick={isMine ? tickFor(m as IChatLocalMessage) : "sent"}
                              myUserId={myUserId}
                              memberNames={room.memberNames}
                              memberImages={room.memberImages}
                              canReply={canSend}
                              canReact={canSend}
                              canShowInfo={!!receiptsFor(m)}
                              canSeeVoters={canSeeVoters}
                              voicePlayed={
                                isMine && m.type === "voice" ? playedByAllIn(detail, m, myUserId) : false
                              }
                              onPlayed={room.markPlayed}
                              onReply={onReply}
                              onEdit={onEdit}
                              onDelete={setToDelete}
                              onInfo={setInfoFor}
                              onRetry={room.retry}
                              onDiscard={room.discard}
                              onVote={room.vote}
                              onReact={room.react}
                              onMessageUser={messageUser}
                              onOpenImage={openImages}
                              onCopy={copy}
                              onJumpToMessage={showMessage}
                              canPin={canSend}
                              onTogglePin={room.togglePin}
                              onForward={setForwarding}
                              onOpenSender={openSender}
                              onCreateTicket={startTicket}
                              selecting={!!ticketPick}
                              selected={!!ticketPick?.has(m.id)}
                              onToggleSelect={toggleTicketPick}
                            />
                          </div>
                        </Fragment>
                      );
                    })}
                  </section>
                ))
              )}
              {room.isLoadingNewer ? (
                <div className="flex justify-center py-2">
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
              ) : null}
            </>
          )}
        </div>
        {room.isJumping ? (
          <div className="pointer-events-none absolute inset-x-0 top-3 z-20 flex justify-center">
            <Skeleton className="h-6 w-24 rounded-full shadow-xs" />
          </div>
        ) : null}
        {searchOpen ? (
          <MessageSearchResults
            conversationId={conversationId}
            query={searchQuery}
            myUserId={myUserId}
            onOpen={openResult}
            onClose={closeSearch}
          />
        ) : null}
      </div>

      {/* Floating jumps: my oldest unread mention above, the latest below. */}
      {!searchOpen && (mentionQueue.count > 0 || !atBottom || room.isDetached) ? (
        <div className="pointer-events-none absolute end-5 bottom-28 z-20 flex flex-col items-center gap-2">
          {mentionQueue.count > 0 ? (
            <Button
              variant="primary"
              size="icon-sm"
              onClick={goToMention}
              aria-label={mentionLabel}
              title={mentionLabel}
              className="pointer-events-auto relative rounded-full shadow-md"
            >
              <AtSign className="size-4" />
              {mentionQueue.count > 1 ? (
                <span
                  aria-hidden
                  className="absolute -end-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-background px-1 text-[10px] font-semibold leading-none text-primary ring-1 ring-primary"
                >
                  {formatNumber(mentionQueue.count)}
                </span>
              ) : null}
            </Button>
          ) : null}
          {!atBottom || room.isDetached ? (
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => void goToLatest()}
              isLoading={room.isJumping && room.isDetached}
              aria-label={
                room.hasUnseenNew ? t("actions.jump_to_latest_new") : t("actions.jump_to_latest")
              }
              title={t("actions.jump_to_latest")}
              className="pointer-events-auto relative rounded-full bg-background shadow-md"
            >
              <ArrowDown className="size-4" />
              {room.hasUnseenNew ? (
                <span
                  aria-hidden
                  className="absolute -end-0.5 -top-0.5 size-2.5 rounded-full bg-primary ring-2 ring-background"
                />
              ) : null}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Banners + composer */}
      {banner ? (
        <div className="border-t border-warning/30 bg-warning-muted px-4 py-1.5 text-center text-xs font-medium text-warning">
          {banner}
        </div>
      ) : null}
      {detail && !canSend ? (
        <div className="flex items-center justify-center gap-2 border-t border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          {isDirect ? <UserX className="size-4" /> : <Lock className="size-4" />}
          {isDirect
            ? t("labels.peer_inactive")
            : detail.type === "channel"
              ? channelReadOnly(t, channelOf(detail))
              : t("labels.broadcast_read_only")}
        </div>
      ) : null}
      {detail && scheduled.items.length ? (
        <ScheduledBar
          count={scheduled.items.length}
          failedCount={scheduled.failedCount}
          onOpen={() => setScheduledOpen(true)}
        />
      ) : null}
      {ticketPick ? (
        // Picking messages for a ticket: this bar stands in for the composer.
        <div
          className="flex items-center gap-2 border-t border-border bg-background px-3 py-2.5 md:px-5"
          onKeyDown={(e) => {
            if (e.key === "Escape") endTicket();
          }}
        >
          <SquareKanban className="size-4 shrink-0 text-primary" />
          <span className="flex-1 truncate text-sm text-foreground">
            {t("ticket_from_chat.selected", { count: ticketPick.size })}
          </span>
          <Button variant="ghost" size="sm" onClick={endTicket}>
            {t("ticket_from_chat.cancel")}
          </Button>
          <Button variant="primary" size="sm" disabled={!ticketPick.size} onClick={() => setTicketOpen(true)}>
            {t("ticket_from_chat.next")}
          </Button>
        </div>
      ) : detail && canSend ? (
        <Composer ref={composerRef} room={room} compact={inWindow} />
      ) : null}

      {dragging ? (
        <div className="pointer-events-none absolute inset-2 z-30 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-primary/10 text-primary">
          <Paperclip className="size-6" />
          <span className="text-sm font-semibold">{t("composer.drop_files")}</span>
        </div>
      ) : null}

      {detail ? (
        <ChatInfoDrawer
          open={infoOpen}
          detail={detail}
          myUserId={myUserId}
          onClose={() => setInfoOpen(false)}
          onMessageUser={(id) => void messageUser(id)}
        />
      ) : null}

      <ScheduledMessagesDrawer
        open={scheduledOpen}
        onClose={closeScheduled}
        scheduled={scheduled}
      />

      <ForwardDialog message={forwarding} onClose={() => setForwarding(null)} />
      <CreateTicketDialog
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        onCreated={endTicket}
        messages={ticketPick ? messages.filter((m) => ticketPick.has(m.id)) : []}
        conversation={detail}
      />
      <ProfilePopup
        conversation={profile?.subject ?? null}
        onClose={() => setProfile(null)}
        onOpenImage={(url) => openImages([url], 0)}
        // Message · Info, like the list's popup. A sender: our direct chat
        // with them (Info: with its Chat info open). This chat (the header
        // picture): Message just returns to it, Info opens its drawer.
        onMessage={(id) => (isThisChat ? undefined : void messageUser(id))}
        onInfo={(id) => (isThisChat ? setInfoOpen(true) : void messageUser(id, true))}
      />
      <MessageInfoDialog
        // The live copy: "Played by" grows while it's open.
        message={infoFor ? (messages.find((x) => x.id === infoFor.id) ?? infoFor) : null}
        receipts={infoFor ? receiptsFor(infoFor) : null}
        onClose={() => setInfoFor(null)}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("actions.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("actions.delete_message")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("composer.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger text-danger-foreground hover:bg-danger/90"
              onClick={() => {
                const target = toDelete;
                setToDelete(null);
                if (target) void room.remove(target);
              }}
            >
              {t("actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

const MessagesSkeleton = () => (
  <div className="flex flex-col gap-3 px-5">
    {[60, 40, 70, 35, 55].map((w, i) => (
      <div key={i} className={cn("flex", i % 2 ? "justify-end" : "justify-start")}>
        <Skeleton className="h-10 rounded-2xl" style={{ width: `${w}%` }} />
      </div>
    ))}
  </div>
);
